import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { columns } from './grid-columns';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { officeApiKeyStore } from '../../../../api/office-api-key-store';
import { OfficeCustomGridCell } from '../../../offices/office-custom-grid-cell';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { useEffect, useState } from 'react';
import { State } from '@progress/kendo-data-query';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import useDebounce from '../../../../hooks/use-debounce';
import { Office } from '../../../../models/office';
import { useSaveData } from '../../../../hooks/useSaveData';

interface OfficesTabProps {
  customerID: number;
}

export const OfficesTab = ({ customerID }: OfficesTabProps) => {
  const navigate = useNavigate();

  const handleCreateOffice = () => {
    navigate(`/customers/details/${customerID}/offices/create`);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Customer Details', 'Office'));

  const {
    isLoading,
    data: Offices,
    refetch,
  } = useLoadData<ApiResponse<Office[]>>(officeApiKeyStore.getCustomerOffices(customerID));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredOffices, setFilteredOffices] = useState<Office[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredOfficeList = Offices?.data?.filter((Office) =>
      Object.values(Office).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredOffices(filteredOfficeList || []);
  }, [debouncedSearchTerm, Offices?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Office',
      pageName: 'Customer Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Office',
      pageName: 'Customer Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <div className="borderTop">
      <GuardedComponent requiredPermissions={[UserPermissionName.ADD_OFFICE]}>
        <Button
          className="innerTab-button"
          size="small"
          variant="contained"
          disableElevation={true}
          startIcon={<AddIcon />}
          onClick={handleCreateOffice}
        >
          Add Office
        </Button>
      </GuardedComponent>
      <SiTrackGrid
        fileTitle='customer_office'
        dataItemKey="officeID"
        columns={columns}
        customCell={(field) =>
          OfficeCustomGridCell({
            field,
          })
        }
        // queryConfig={officeApiKeyStore.getCustomerOffices(customerID)}
        viewRoute={(office) => `/offices/details/${office.officeID}`}
        actionPermissions={{
          view: UserPermissionName.VIEW_OFFICE,
          export: UserPermissionName.DOWNLOAD_GRID,
        }}
        // -----------------Grid Layout-------
        dataItems={filteredOffices || []}
        reorderable
        handleSearch={handleInputChange}
        handleSaveLayout={handleSaveLayout}
        isSaveLayout={true}
        gridConfigFromApi={gridConfig}
        handleResetLayout={handleResetLayout}
      // -----------------Grid Layout-------
      />
    </div>
  );
};
